import React, { useState } from "react";
import {
  Avatar,
  Card,
  CardContent,
  Stack,
  SvgIcon,
  Typography,
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
// ---------- icons -----------------
import GroupIcon from "@mui/icons-material/Group";
import AddCardIcon from "@mui/icons-material/AddCard";
import AddModeratorIcon from "@mui/icons-material/AddModerator";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";

interface SubDetail {
  label: string;
  value: string | number;
  color?: string;
}

export interface DetailCardView {
  label?: string;
  value?: any;
  subDetails: SubDetail[];
}

function DetailCard({
  sx,
  themeColor,
  title,
  icon,
  views,
  handleClick,
  calendarFilter
}: {
  sx?: any;
  themeColor:
    | "success"
    | "warning"
    | "error"
    | "secondary"
    | "primary"
    | "info";
  title: string;
  icon: "PersonAddIcon" | "AddCardIcon" | "AddModeratorIcon" | "GroupIcon";
  calendarFilter?:boolean;
  views: {
    daily: DetailCardView;
    weekly: DetailCardView;
    monthly: DetailCardView;
  };
  handleClick?: () => void;
}) {
  const [activeView, setActiveView] = useState<"daily" | "weekly" | "monthly">(
    "daily",
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleSelectView = (view: "daily" | "weekly" | "monthly") => {
    setActiveView(view);
    handleCloseMenu();
  };

  const currentView = views[activeView];

  const clickHandler = () => {
    if (handleClick) {
      handleClick();
    }
  };

  return (
    <Card
      sx={{
        ...sx,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        boxShadow: "0 2px 12px 0 rgba(0,0,0,0.03)",
        overflow: "hidden",
        position: "relative",
        transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 8px 24px 0 rgba(0,0,0,0.08)",
        },
        "&::after": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "4px",
          backgroundColor: `${themeColor}.main`,
        },
      }}
    >
      <CardContent sx={{ p: "20px !important" }}>
        <Stack
          alignItems="center"
          direction="row"
          justifyContent="space-between"
          spacing={2}
        >
          <Stack spacing={0.5}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography
                color="text.secondary"
                variant="overline"
                sx={{
                  fontWeight: 700,
                  letterSpacing: 1.1,
                  lineHeight: 1.2,
                  fontSize: "0.65rem",
                }}
              >
                {title} {currentView.label && `(${currentView.label})`}
              </Typography>
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleCloseMenu}
                transformOrigin={{ horizontal: "left", vertical: "top" }}
                anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
              >
                <MenuItem
                  onClick={() => handleSelectView("daily")}
                  selected={activeView === "daily"}
                >
                  Daily View
                </MenuItem>
                <MenuItem
                  onClick={() => handleSelectView("weekly")}
                  selected={activeView === "weekly"}
                >
                  Weekly View
                </MenuItem>
                <MenuItem
                  onClick={() => handleSelectView("monthly")}
                  selected={activeView === "monthly"}
                >
                  Monthly View
                </MenuItem>
              </Menu>
            </Stack>
            <div className="flex justify-start items-start gap-3">
              <Typography
                variant="h4"
                sx={{ fontWeight: 800, color: "text.primary" }}
              >
                {currentView.value}
              </Typography>
              {calendarFilter && <IconButton
                size="large"
                onClick={handleOpenMenu}
                sx={{ p: 0, color: "text.secondary" }}
              >
                <CalendarMonthIcon sx={{ fontSize: "1.2rem" }} />
              </IconButton>}
            </div>
          </Stack>
          <Avatar
            onClick={clickHandler}
            sx={{
              background: (theme: any) =>
                `linear-gradient(135deg, ${theme.palette[themeColor].light} 0%, ${theme.palette[themeColor].main} 100%)`,
              height: 44,
              width: 44,
              cursor: "pointer",
              boxShadow: (theme: any) =>
                `0 6px 12px -4px ${theme.palette[themeColor].main}60`,
            }}
          >
            <SvgIcon sx={{ fontSize: 22, color: "#fff" }}>
              {icon == "PersonAddIcon" && <PersonAddIcon />}
              {icon == "AddCardIcon" && <AddCardIcon />}
              {icon == "AddModeratorIcon" && <AddModeratorIcon />}
              {icon == "GroupIcon" && <GroupIcon />}
            </SvgIcon>
          </Avatar>
        </Stack>

        {currentView.subDetails.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ mb: 1.5, opacity: 0.5 }} />
            <Stack
              direction="row"
              spacing={3}
              justifyContent="flex-start"
              sx={{ flexWrap: "wrap" }}
            >
              {currentView.subDetails.map((detail, index) => (
                <Box key={index}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ fontSize: "0.65rem", fontWeight: 600, mb: 0.2 }}
                  >
                    {detail.label.toUpperCase()}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      color: detail.color || "text.primary",
                    }}
                  >
                    {detail.value}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default DetailCard;